import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const fmtUsd = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtAge = (min) => {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  if (min < 1440) return `${Math.floor(min / 60)}h ${min % 60}m`;
  return `${Math.floor(min / 1440)}d ${Math.floor((min % 1440) / 60)}h`;
};

export async function reconcileInvoice(invoiceId) {
  const r = await fetch(`${API_URL}/api/owner-admin/invoices/${encodeURIComponent(invoiceId)}/reconcile-stripe`, {
    method: 'POST',
    credentials: 'include',
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fallback = r.status >= 500 ? 'Stripe lookup failed — session not found or Stripe unreachable' : `Reconcile failed (${r.status})`;
    throw new Error(body.detail || fallback);
  }
  return body;
}

export async function runSweepNow() {
  const r = await fetch(`${API_URL}/api/owner-admin/invoices/stuck/sweep`, { method: 'POST', credentials: 'include' });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.detail || `Sweep failed (${r.status})`);
  return body;
}

export function useStuckInvoices({ checkStripe = true, limit = 25 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/owner-admin/invoices/stuck?check_stripe=${checkStripe ? 1 : 0}&limit=${limit}`,
        { credentials: 'include' },
      );
      setData(r.ok ? await r.json() : null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [checkStripe, limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, refresh };
}

export function StripeStatusBadge({ status }) {
  if (status === 'paid') {
    return (
      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 gap-1" data-testid="stuck-badge-paid">
        <AlertTriangle className="h-3 w-3" /> Paid on Stripe
      </Badge>
    );
  }
  if (status === 'unpaid') return <Badge className="bg-white/10 text-slate-300 border-white/20">Unpaid</Badge>;
  if (status === 'not_found') {
    return <Badge variant="outline" className="border-white/20 text-slate-400">Not found on Stripe</Badge>;
  }
  if (status === 'error' || status === 'unavailable') {
    return <Badge variant="outline" className="border-amber-500/30 text-amber-300">Stripe unreachable</Badge>;
  }
  if (!status) return <Badge variant="outline" className="border-white/20 text-slate-400">Not checked</Badge>;
  return <Badge variant="outline" className="border-white/20 text-slate-300 capitalize">{status}</Badge>;
}

export function RecheckStripeButton({ invoiceId, onDone, size = 'sm', className = '' }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await reconcileInvoice(invoiceId);
      if (res.reconciled && !res.already_recorded) {
        toast.success(`Invoice marked paid — ${fmtUsd(res.invoice?.amount_paid)} recorded`);
      } else if (res.reconciled && res.already_recorded) {
        toast.info('Payment was already recorded — invoice is paid');
      } else {
        toast.info(res.message || `Stripe reports: ${res.stripe_payment_status || 'unpaid'}`);
      }
      onDone?.(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size={size}
      variant="outline"
      onClick={run}
      disabled={busy}
      className={`border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 ${className}`}
      data-testid={`recheck-stripe-${invoiceId}`}
    >
      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Checking…' : 'Re-check Stripe'}
    </Button>
  );
}
